import { type RefObject, useEffect, useState } from "react";

import type { AllOverlayTriggerState, OverlayPlacement } from "../types/overlay.d.ts";

const ANIMATION_OFFSET = 5;

/**
 * @deprecated Use react-spring instead
 */
export const useAnimeState = (
  ref: RefObject<Element | null>,
  state: AllOverlayTriggerState,
  placement: OverlayPlacement,
): AllOverlayTriggerState => {
  const [isAnimeOpen, setAnimeOpen] = useState(false);
  useEffect(() => {
    if (state.isOpen) {
      // show DOM as soon as the state is open.
      setAnimeOpen(true);
    }
  }, [state.isOpen]);

  useEffect(() => {
    if (!ref.current) {
      setAnimeOpen(state.isOpen); // if there is no ref then we simply align the animeOpen to isOpen
      return;
    }

    const theOpenState = state.isOpen;

    let x = 0;
    let y = 0;

    switch (placement) {
      case "bottom":
        y = -ANIMATION_OFFSET;
        break;
      case "top":
        y = ANIMATION_OFFSET;
        break;
      case "left":
        x = ANIMATION_OFFSET;
        break;
      case "right":
        x = -ANIMATION_OFFSET;
    }

    void ref.current
      .animate(
        [
          {
            transform: `translate3d(${state.isOpen ? x : 0}px, ${state.isOpen ? y : 0}px, 0)`,
            opacity: state.isOpen ? 0 : 1,
          },
          {
            transform: `translate3d(${state.isOpen ? 0 : x}px, ${state.isOpen ? 0 : y}px, 0)`,
            opacity: state.isOpen ? 1 : 0,
          },
        ],
        {
          duration: 180,
          easing: "ease-out",
          fill: "forwards", // Keep the changes applied once finished
        },
      )
      .finished.then((anim) => {
        try {
          // Apply the styles to the element
          anim.commitStyles();
          anim.cancel();

          // only remove DOM once the animation is finished
          if (!theOpenState) setAnimeOpen(false);
        } catch {
          // Do nothing.
          // It may break if the element is unmounted before the end of the animation.
        }
      });
  }, [state.isOpen, placement, ref]);

  return { ...state, isOpen: state.isOpen || isAnimeOpen };
};
